"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import Image from "next/image";
import { CAREER_CARDS_GOVERNMENT_DATA } from "./constants";
import CareerStatComponent from "./CareerStat";
import CareerKeySkills from "./CareerKeySkills";
import { CareerStat } from "./types";
import Icon from "../icons";
import Tag from "@/components/Tags/Tag";
import useTranslations from "@/i18n/useTranslations";

const DESKTOP_BREAKPOINT = 1024;
const MOBILE_BREAKPOINT = 665;
const MAX_WIDTH_BREAKPOINT = 824;
const HUMAN_IMAGE_WIDTH = 472;

export interface CareerCardsProps {
  variant?: "default" | "male-variant" | "female-variant";
  heading?: string;
  title: string;
  stats: CareerStat[];
  skills?: string[];
  ctaText?: string;
  ctaHref?: string;
  hiddenCta?: boolean;
  className?: string;
  industry?:
    | "dataScience"
    | "businessManagement"
    | "liberalArt"
    | "computerScience"
    | "socialSciences"
    | "healthcare"
    | "other";
  isSideRail?: boolean;
  location?: string;
}

export interface CareerCardsVariant extends Omit<CareerCardsProps, "variant"> {
  variantKey?: "default" | "male-variant" | "female-variant";
}

export default function CareerCards({
  variant = "default",
  heading,
  title,
  stats,
  skills = [],
  ctaText,
  ctaHref = "#",
  industry = "dataScience",
  hiddenCta = false,
  className,
  isSideRail = false,
  location = "detail-page",
}: CareerCardsProps) {
  const t = useTranslations("Common");
  const resolvedCtaText = ctaText ?? t("browseRelatedCourses");
  const [isLargeEnough, setIsLargeEnough] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const { elementClicked, elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.CareerCard,
      htmlId: "career-card",
      location,
      name: "career-card",
      text: industry,
    },
  });

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const viewedRef = useRef(false);

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      inViewRef(node);

      if (node) {
        const checkComponentWidth = () => {
          const width = node.offsetWidth;
          if (width === MAX_WIDTH_BREAKPOINT) {
            setIsLargeEnough(false);
          } else if (
            width >= DESKTOP_BREAKPOINT ||
            width <= MOBILE_BREAKPOINT
          ) {
            setIsLargeEnough(true);
          } else {
            setIsLargeEnough(false);
          }
        };

        checkComponentWidth();

        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }

        const resizeObserver = new ResizeObserver(checkComponentWidth);
        resizeObserver.observe(node);
        resizeObserverRef.current = resizeObserver;
      } else {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
      }
    },
    [inViewRef],
  );

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  const effectiveIsSideRail = isSideRail && !isLargeEnough;

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }
  }, [inView, elementViewed]);

  const handleCtaClick = () => {
    elementClicked({
      webElement: {
        elementType: ElementType.CareerCard,
        htmlId: "career-card",
        location,
        name: "career-card",
        text: industry,
      },
      outboundUrl: ctaHref,
    });
  };

  if (variant !== "default") {
    return (
      <div
        ref={combinedRef}
        className="xl:max-w-[1331px] mx-auto w-full flex flex-col md:px-4 xl:px-0 md:mt-12 relative border border-putty-400 bg-white overflow-hidden rounded-2xl md:h-[378px] md:justify-center"
      >
        {/* Optional Heading */}
        {heading && (
          <h2 className={"text-[30px] lg:text-3xl font-bold mb-4 lg:mb-6 mt-0"}>
            {heading}
          </h2>
        )}
        <div
          className={cn(
            "from-primary bg-transparent rounded-2xl p-6 md:p-8 text-white z-[1]",
            className,
            effectiveIsSideRail && "p-8",
          )}
        >
          {/* Title */}
          <h2
            className={cn(
              "md:block text-primary-500 font-bold mt-0 mb-4 md:text-lg lg:text-xl",
              effectiveIsSideRail && "mb-6",
            )}
          >
            {title}
          </h2>

          {/* Stats Grid */}
          <div
            className={cn(
              "grid grid-cols-1 md:grid-cols-2 gap-4 mb-6",
              !effectiveIsSideRail && "max-w-[744px]",
            )}
          >
            {stats.slice(0, 2).map((stat, index) => {
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-4 text-primary flex flex-row items-start md:items-start gap-4 border border-putty-400"
                >
                  <div
                    className={cn(
                      "lg:block self-center sm:hidden md:block",
                      effectiveIsSideRail && "md:hidden",
                    )}
                  >
                    <div className="flex items-center justify-center">
                      <Icon
                        type={stat.icon}
                        svgProps={{
                          className: "w-10 h-10 lg:w-12 lg:h-12 color-elm-500",
                        }}
                      />
                    </div>
                  </div>
                  {/* Vertical divider - desktop only */}
                  <div
                    className={cn(
                      "lg:block w-px bg-putty-400 self-stretch mx-2 sm:hidden md:block",
                      effectiveIsSideRail && "md:hidden",
                    )}
                  ></div>
                  <div className="flex-1">
                    <p className="text-lg font-bold text-primary-500 mb-1">
                      {stat.label}
                    </p>
                    <p className="text-3xl md:text-xl lg:text-2xl font-bold mb-0 text-primary-500">
                      {stat.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Skills, Button, and Disclaimer Container */}
          <div className="flex flex-col md:flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Left side: Skills and Disclaimer */}
            <div className="flex-1">
              {/* Skills */}
              {skills.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-row flex-wrap items-center gap-2">
                    <p className="text-sm text-primary-500 font-semibold mb-0">
                      Key skills
                    </p>
                    {skills.map((skill, index) => (
                      <Tag
                        key={index}
                        title={skill}
                        color="fern"
                        className="px-2 py-1 text-xs font-bold [border-radius:4px]"
                      />
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs md:text-sm text-gray/800 mb-0">
                This information is US-specific and can be found at the{" "}
                <a
                  href={
                    CAREER_CARDS_GOVERNMENT_DATA[
                      industry as keyof typeof CAREER_CARDS_GOVERNMENT_DATA
                    ]
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray/800"
                >
                  Bureau of Labor and Statistics
                </a>{" "}
                as of 2025 .
              </p>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 rounded-2xl hidden lg:block">
          {!effectiveIsSideRail && (
            <Image
              src={`/images/human-${variant === "male-variant" ? "male" : "female"}.jpg`}
              width={HUMAN_IMAGE_WIDTH}
              height={378}
              alt={`${variant === "male-variant" ? "Male" : "Female"}`}
              className={cn(
                "w-[472px] h-full object-cover object-center absolute top-0 right-0",
                containerRef.current &&
                  containerRef.current.offsetWidth <= MAX_WIDTH_BREAKPOINT &&
                  "opacity-75",
                containerRef.current &&
                  containerRef.current.offsetWidth <= MAX_WIDTH_BREAKPOINT &&
                  "scale-crop",
              )}
              style={{ pointerEvents: "none" }}
            />
          )}
          {effectiveIsSideRail && (
            <Image
              src={`/images/Human-${variant === "male-variant" ? "Male" : "Female"}-Light.jpg`}
              width={HUMAN_IMAGE_WIDTH}
              height={378}
              alt={`${variant === "male-variant" ? "Male" : "Female"}`}
              className={cn(
                "w-[100%] h-full object-cover object-center absolute top-0 right-0",
              )}
              style={{ pointerEvents: "none" }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={combinedRef}
      className="xl:max-w-[1331px] mx-auto w-full flex flex-col 2  px-4 xl:px-0 mt-12"
    >
      {/* Optional Heading */}
      {heading && (
        <h2 className="text-[30px] lg:text-3xl font-bold mb-4 lg:mb-6 mt-0">
          {heading}
        </h2>
      )}
      <div
        className={cn(
          "bg-gradient-to-br from-primary bg-primary rounded-2xl p-6 md:p-8 text-white",
          className,
        )}
      >
        {/* Title */}
        <h2 className="md:block text-white font-bold mt-0 mb-4 md:text-lg lg:text-xl">
          {title}
        </h2>

        {/* Stats Grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-${stats.length} gap-4 mb-6`}
        >
          {stats.map((stat, index) => (
            <CareerStatComponent key={index} {...stat} />
          ))}
        </div>

        {/* Skills, Button, and Disclaimer Container */}
        <div className="flex flex-col md:flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Left side: Skills and Disclaimer */}
          <div className="flex-1">
            {/* Skills */}
            <CareerKeySkills skills={skills} />

            {/* CTA Button - Mobile and Tablet only */}
            {!hiddenCta && (
              <div className="lg:hidden mb-4">
                <Button
                  asChild
                  variant="brand"
                  size="xl"
                  className="w-full md:w-auto bg-brand hover:bg-brand-hover border-brand"
                >
                  <Link href={ctaHref} onClick={handleCtaClick}>
                    {resolvedCtaText}
                  </Link>
                </Button>
              </div>
            )}

            <p className="text-xs text-white md:text-sm text-white/80 mb-0">
              This information is US-specific and can be found at the{" "}
              <a
                href={
                  CAREER_CARDS_GOVERNMENT_DATA[
                    industry as keyof typeof CAREER_CARDS_GOVERNMENT_DATA
                  ]
                }
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                Bureau of Labor and Statistics
              </a>{" "}
              as of 2025.
            </p>
          </div>

          {/* Right side: CTA Button - Desktop only */}
          {!hiddenCta && (
            <div className="hidden lg:block lg:self-center">
              <Button
                asChild
                variant="brand"
                size="lg"
                className="w-auto bg-brand hover:bg-brand-hover border-brand"
              >
                <Link
                  href={ctaHref}
                  onClick={handleCtaClick}
                  className="no-underline hover:no-underline"
                >
                  {resolvedCtaText}
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
